import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { dataImportAdminRef } from '../lib/db';
import { useActiveTenantId } from '../lib/tenant';
import type { DataImportAdminSettings } from '../types';

/** Live tenant-scoped Import Management config (tenants/{tenantId}/
 *  dataImportAdmin/config — see dataImportAdminRef() in ../lib/db).
 *  `settings` is null until the doc loads AND whenever it hasn't been
 *  saved yet for this tenant — callers (effectiveReportImportSettings())
 *  already treat null as "use Compass's own hardcoded defaults," so this
 *  never needs a DEFAULT_* fallback object the way useSettings() does.
 *
 *  firestore.rules restricts this doc to admins only (see the file's own
 *  rules comment) — a non-admin caller's snapshot listener will error,
 *  which the onSnapshot error callback below turns into loading:false /
 *  settings:null rather than an unhandled rejection. In practice this
 *  hook should only ever be mounted from the Settings page, which is
 *  already admin-route-gated. */
export function useDataImportAdminSettings(): { settings: DataImportAdminSettings | null; loading: boolean } {
  const [settings, setSettings] = useState<DataImportAdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const activeTenantId = useActiveTenantId();

  useEffect(() => {
    setLoading(true);
    return onSnapshot(
      dataImportAdminRef(),
      (snap) => {
        setSettings(snap.exists() ? (snap.data() as DataImportAdminSettings) : null);
        setLoading(false);
      },
      () => {
        setSettings(null);
        setLoading(false);
      },
    );
  }, [activeTenantId]);

  return { settings, loading };
}
